import React, { useEffect, useCallback } from "react";
import { usePrevious } from "../../hooks";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  FormHelperText,
  InputLabel,
} from "@mui/material";
import { Delete as DeleteIcon, Send as SendIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Field } from "react-final-form";

import { TextField } from "../common/Fields";
import { Content } from "../layout";
import {
  getCommunicator,
  submitCommunicator,
  updateCommunicator,
  deleteCommunicator,
  fetchOrgs,
} from "../../actions";

const CommunicatorForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { communicator } = useSelector((state) => state.communicator);
  const { user } = useSelector((state) => state.auth);
  const prevOrganization = usePrevious(user?.Organization?.id);

  useEffect(() => {
    dispatch(fetchOrgs({}));
    if (id) {
      dispatch(getCommunicator(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (user && prevOrganization) {
      if (user.Organization.id !== prevOrganization) {
        navigate("/communicators");
      }
    }
  }, [user, prevOrganization, navigate]);

  const onSubmit = useCallback(
    async (communicator) => {
      const upsertCommunicator = new Promise((resolve, reject) => {
        if (id) {
          dispatch(updateCommunicator({ communicator, id }))
            .then(() => resolve())
            .catch((error) => reject(error));
        } else {
          dispatch(submitCommunicator({ communicator }))
            .then(() => resolve())
            .catch((error) => reject(error));
        }
      });

      try {
        await upsertCommunicator;
        navigate("/communicators");
      } catch (error) {
        let errors;
        if (error.response.data?.errors) {
          const arrayErrors = error.response.data.errors;
          arrayErrors.forEach((error) => {
            errors = { ...errors, [error.param]: error.msg };
          });
        }
        return errors;
      }
    },
    [id, dispatch, navigate]
  );

  const onDelete = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(deleteCommunicator({ id }, navigate));
    },
    [id, dispatch, navigate]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        OrganizationId: user?.Organization?.id,
        chipID: communicator?.chipID,
        name: communicator?.name,
        description: communicator?.description,
      }}
      validate={({ chipID, name, description }) => {
        const errors = {};
        if (!chipID) {
          errors.chipID = "필수";
        } else if (!Number.isInteger(parseInt(chipID))) {
          errors.chipID = "숫자로 입력해주세요";
        }
        if (!name) {
          errors.name = "필수";
        }
        if (!description) {
          errors.description = "필수";
        }
        return errors;
      }}
      render={({
        submitError,
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
      }) => (
        <Content>
          <form onSubmit={handleSubmit}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  안전장비
                </Typography>
                <Grid
                  container
                  alignItems="flex-start"
                  spacing="20"
                  sx={{ mt: 2 }}
                >
                  <Grid item xs={12}>
                    {user && (
                      <Box>
                        <InputLabel shrink={true}>건설사</InputLabel>
                        <Box>{user.Organization.displayName}</Box>
                        <Field
                          name="OrganizationId"
                          type="hidden"
                          render={({ input, meta }) => <input {...input} />}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="chipID"
                      type="text"
                      label="칩 ID"
                      helperText="숫자만 입력할 수 있습니다."
                      inputProps={{
                        pattern: "^\\d+$",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="name"
                      type="text"
                      label="장치명"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="description"
                      type="text"
                      label="장치설명"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {submitError && <FormHelperText>{submitError}</FormHelperText>}
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={6}>
                {id && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                  >
                    삭제
                  </Button>
                )}
              </Grid>
              <Grid item xs={6} container justifyContent="flex-end">
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  type="submit"
                >
                  저장
                </Button>
              </Grid>
            </Grid>
          </form>
        </Content>
      )}
    />
  );
};

export default CommunicatorForm;
